import { useState, useCallback } from 'react';
import { API } from '@/utils/API';
import { SubmissionFormData } from '@/features/submission_forms/types';

interface UseSubmissionFormResult {
  fetchSubmissionForm: () => Promise<void>;
  submissionFormData: SubmissionFormData | null;
  isLoading: boolean;
  error: string | null;
}

const fetchSubmissionFormAction = async (submissionId: string | undefined) => {
  if (!submissionId) {
    throw new Error('No form ID provided');
  }

  const endpoint = `/submissions/${submissionId}`;
  const response = await API.authenticated.fetch(endpoint);

  if (!response.ok) {
    throw new Error('Failed to fetch form');
  }

  return response.json();
};

export const useSubmissionForm = (
  submissionId: string | undefined
): UseSubmissionFormResult => {
  const [submissionFormData, setSubmissionFormData] =
    useState<SubmissionFormData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSubmissionForm = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await fetchSubmissionFormAction(submissionId);
      setSubmissionFormData(data);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to fetch form';
      setError(errorMessage);
      setSubmissionFormData(null);
    } finally {
      setIsLoading(false);
    }
  }, [submissionId]);

  return {
    fetchSubmissionForm,
    submissionFormData,
    isLoading,
    error,
  };
};
